/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.14.0
 */

import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { makeSelectBreadCrumbsData } from '@/main/modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@/main/modules/breadCrumbs/utils/breadCrumbsGoBack';
import { ModalMultipleOrders } from '@/main/modules/modal/components/ModalMultipleOrders';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { ETextBlockStatus } from '@components/common/TextBlockStatus/textBlockStatusTypes';
import { HttpStatus } from '@modules/app/appConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardAdditionalAccounts,
} from '@modules/internationalization/intSelectors';
import { IWizardAdditionalAccountsTranslation } from '@modules/internationalization/types/wizards/additionalAccounts';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import {
    wizardReportStorageRestoreSaga,
    wizardReportValidateSaga,
} from '@modules/wizard/components/WizardReport/wizardReportSagas';
import { makeSelectWizardReportOrderParams } from '@modules/wizard/components/WizardReport/wizardReportSelectors';
import { IWizardReportOrderParams } from '@modules/wizard/components/WizardReport/wizardReportTypes';
import {
    wizardNextAction,
    wizardOpenModalSummaryAction,
    wizardStorageSaveStepAction,
    wizardStorageUpdateStepAction,
} from '@modules/wizard/wizardActions';
import {
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
} from '@modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '@modules/wizard/wizardSagas';
import { makeSelectWizardBasketSnapshotId, makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { TWizardMetaSuccessAction, TWizardNextSuccessAction } from '@modules/wizard/wizardTypes';

import { ICommonTranslation } from '../../modules/internationalization/types';
import ModalErrorCommon from '../../modules/modal/components/ModalError';
import {
    makeSelectAuthoritiesIsServiceUser,
    makeSelectIsFeatureWithVersionEnabled,
} from '../PageAuth/selectors/authSelectors';
import { makeSelectWizardChangeLockRemovedSubscribers } from '../WizardChangeLock/wizardChangeLockSelectors';
import {
    wizardAdditionalAccountsSetParamsAction,
    wizardAdditionalAccountsSetStepValidAction,
} from './wizardAdditionalAccountsActions';
import {
    EWizardAdditionalAccountsOperationType,
    EWizardAdditionalAccountsSteps,
    initWizardAdditionalAccountsState,
    WIZARD_ADDITIONAL_ACCOUNTS_HANDLE_NEXT,
    WIZARD_ADDITIONAL_ACCOUNTS_SET_PARAMS,
    WIZARD_ADDITIONAL_ACCOUNTS_START_VALIDATE,
} from './wizardAdditionalAccountsConstant';
import {
    IWizardAdditionalAccountsOrderResult,
    IWizardAdditionalAccountsReducer,
    IWizardAdditionalAccountsSelectedTypes,
} from './wizardAdditionalAccountsReducer';
import {
    makeSelectWizardAdditionalAccountsIsAgreementCheckboxEnabled,
    makeSelectWizardAdditionalAccountsIsOnlineCheckProceeded,
    makeSelectWizardAdditionalAccountsOrderResult,
    makeSelectWizardAdditionalAccountsRemovedSubscribers,
    makeSelectWizardAdditionalAccountsSelectedTypes,
    makeSelectWizardAdditionalAccountsSubscriberIds,
} from './wizardAdditionalAccountsSelectors';

/*
 * -------------------------------------------
 *           INITIALIZING
 * -------------------------------------------
 */

export function* wizardAdditionalAccountsMetaSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    const localStorageParams = yield call(wizardAdditionalAccountsLoadDataFromStorageSaga);

    switch (stepName) {
        case EWizardAdditionalAccountsSteps.StepSelectParams:
            const offerLink: IWizardAdditionalAccountsReducer['offerLink'] = action.payload.step.offerLink;
            const activateTypes: IWizardAdditionalAccountsReducer['activateTypes'] = action.payload.step.activateTypes;
            const deactivateTypes: IWizardAdditionalAccountsReducer['deactivateTypes'] =
                action.payload.step.deactivateTypes;

            yield put(
                wizardAdditionalAccountsSetParamsAction({
                    activateTypes: activateTypes || [],
                    deactivateTypes: deactivateTypes || [],
                    offerLink: offerLink || '',
                }),
            );
            if (!localStorageParams.selected) {
                const isActivatePersonalAccount =
                    activateTypes && activateTypes.length === 2 ? activateTypes[0]?.isEnabled : false;
                const isActivateContentAccount = activateTypes
                    ? activateTypes.length === 2
                        ? activateTypes[1]?.isEnabled
                        : activateTypes[0]?.isEnabled
                    : false;
                const isDeactivatePersonalAccount = deactivateTypes ? deactivateTypes[0]?.isEnabled : false;
                const isDeactivateContentAccount =
                    deactivateTypes && deactivateTypes.length === 2 ? deactivateTypes[1]?.isEnabled : false;

                yield put(
                    wizardAdditionalAccountsSetParamsAction({
                        selected: {
                            isActivateAdditionalAccounts: isActivatePersonalAccount || isActivateContentAccount,
                            isDeactivateAdditionalAccounts: isDeactivatePersonalAccount || isDeactivateContentAccount,
                            isActivatePersonalAccount: isActivatePersonalAccount,
                            isActivateContentAccount: isActivateContentAccount,
                            isDeactivatePersonalAccount: isDeactivatePersonalAccount,
                            isDeactivateContentAccount: isDeactivateContentAccount,
                        },
                    }),
                );
            }
            break;
        case EWizardAdditionalAccountsSteps.StepConfirmation:
            yield call(wizardReportStorageRestoreSaga, action);
            break;
    }
}

/*
 * -------------------------------------------
 *       SEND DATA ON NEXT STEP
 * -------------------------------------------
 */

export function* wizardAdditionalAccountsHandleNextSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    const selected: IWizardAdditionalAccountsSelectedTypes = yield select(
        makeSelectWizardAdditionalAccountsSelectedTypes(),
    );
    const subscribersIds: number[] = yield select(makeSelectWizardAdditionalAccountsSubscriberIds());

    const params: { [key: string]: string | number | boolean | number[] } = {
        operationType: selected.isActivateAdditionalAccounts
            ? EWizardAdditionalAccountsOperationType.CONNECT
            : EWizardAdditionalAccountsOperationType.DISCONNECT,
        isActivatePersonalAccount: selected.isActivatePersonalAccount,
        isActivateContentAccount: selected.isActivateContentAccount,
        isDeactivatePersonalAccount: selected.isDeactivatePersonalAccount,
        isDeactivateContentAccount: selected.isDeactivateContentAccount,
        subsIds: subscribersIds,
    };

    switch (stepName) {
        case EWizardAdditionalAccountsSteps.StepSelectParams:
            yield put(
                wizardNextAction({
                    data: params,
                }),
            );
            break;
        case EWizardAdditionalAccountsSteps.StepConfirmation:
            const reportParams: IWizardReportOrderParams = yield select(makeSelectWizardReportOrderParams());

            yield put(
                wizardNextAction({
                    data: params,
                    ...reportParams,
                }),
            );
            break;
    }

    yield call(wizardAdditionalAccountsUpdateStorageSaga);
}

export function* wizardAdditionalAccountsHandleNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    switch (stepName) {
        case EWizardAdditionalAccountsSteps.StepSelectParams:
            const isOnlineCheckProceeded: boolean = action.payload.currentStep?.step.isOnlineCheckProceeded;
            const orderResult: IWizardAdditionalAccountsOrderResult = action.payload.currentStep?.step.orderResult;
            yield put(
                wizardAdditionalAccountsSetParamsAction({
                    isOnlineCheckProceeded: isOnlineCheckProceeded,
                    orderResult: orderResult,
                }),
            );
            break;
        case EWizardAdditionalAccountsSteps.StepConfirmation:
            const orderId = action.payload.currentStep?.step?.order?.orderId ?? '';

            const isMegaPowersFeatureGreaterOrEqualThan2 = yield select(
                makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.MEGA_POWERS, 2, true),
            );

            if (isMegaPowersFeatureGreaterOrEqualThan2) {
                const { orderCreationModal }: ICommonTranslation = yield select(makeSelectTranslationCommon());

                const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
                const goBack = () => breadCrumbGoBack(breadCrumbsData);

                yield put(
                    modalOpen({
                        render: () =>
                            createElement(ModalMultipleOrders, {
                                isOpen: true,
                                orderOrOrders: orderId,
                                onClose: goBack,
                                onSuccessButtonClick: breadCrumbGoBack,
                                orderTypeTranlation: orderCreationModal.success.orderType.ADDITIONAL_ACCOUNTS,
                            }),
                    }),
                );
            } else {
                yield put(wizardOpenModalSummaryAction({ orderId }));
            }

            break;
    }

    yield call(wizardAdditionalAccountsLoadDataFromStorageSaga);
}

/*
 * -------------------------------------------
 *       SAVE DATA TO STORAGE
 * -------------------------------------------
 */

export function* wizardAdditionalAccountsUpdateStorageSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    switch (stepName) {
        case EWizardAdditionalAccountsSteps.StepSelectParams:
            const selected = yield select(makeSelectWizardAdditionalAccountsSelectedTypes());
            const isOnlineCheckProceeded = yield select(makeSelectWizardAdditionalAccountsIsOnlineCheckProceeded());
            const orderResult = yield select(makeSelectWizardAdditionalAccountsOrderResult());
            yield put(
                wizardStorageUpdateStepAction({
                    selected,
                    isOnlineCheckProceeded,
                    orderResult,
                }),
            );
            break;
        case EWizardAdditionalAccountsSteps.StepConfirmation:
            const removedSubscribers = yield select(makeSelectWizardAdditionalAccountsRemovedSubscribers());
            yield put(
                wizardStorageUpdateStepAction({
                    removedSubscribers,
                }),
            );
            break;
    }
}

export function* wizardAdditionalAccountsLoadDataFromStorageSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const localStorageParams = localStorageState[basketSnapshotId];

    /**
     * Если обновили страницу обновим всю информацию, которая у нас есть
     */
    if (localStorageParams) {
        const isOnlineCheckProceeded = yield select(makeSelectWizardAdditionalAccountsIsOnlineCheckProceeded());
        const orderResult = yield select(makeSelectWizardAdditionalAccountsOrderResult());
        const removedSubscribers = yield select(makeSelectWizardChangeLockRemovedSubscribers());

        yield put(
            wizardAdditionalAccountsSetParamsAction({
                selected: localStorageParams.selected || initWizardAdditionalAccountsState.selected,
                isAgreementCheckboxEnabled: false,
                isOnlineCheckProceeded: isOnlineCheckProceeded || localStorageParams.isOnlineCheckProceeded,
                orderResult: orderResult?.orderSubs.length ? orderResult : localStorageParams.orderResult,
                removedSubscribers: removedSubscribers?.length
                    ? removedSubscribers
                    : localStorageParams.removedSubscribers || [],
            }),
        );
    }

    yield call(wizardAdditionalAccountsUpdateStorageSaga);

    return localStorageParams;
}

export function* wizardAdditionalAccountsStorageSaveSaga(): SagaIterator {
    const params = {};
    yield put(wizardStorageSaveStepAction({ params }));
}

export function* wizardAdditionalAccountsValidateSaga(): SagaIterator {
    let isStepValid = true;
    let validateError = '';
    const stepName = yield select(makeSelectWizardStepName());
    const translation: IWizardAdditionalAccountsTranslation = yield select(
        makeSelectTranslationWizardAdditionalAccounts(),
    );
    const subscribersIds: number[] = yield select(makeSelectWizardAdditionalAccountsSubscriberIds());
    const selectedTypes: IWizardAdditionalAccountsSelectedTypes = yield select(
        makeSelectWizardAdditionalAccountsSelectedTypes(),
    );

    switch (stepName) {
        case EWizardAdditionalAccountsSteps.StepSelectParams:
            const isAgreementCheckboxEnabled: boolean = yield select(
                makeSelectWizardAdditionalAccountsIsAgreementCheckboxEnabled(),
            );

            isStepValid = selectedTypes.isActivateAdditionalAccounts
                ? selectedTypes.isActivatePersonalAccount
                    ? isAgreementCheckboxEnabled
                    : true
                : selectedTypes.isDeactivateAdditionalAccounts;
            break;
        case EWizardAdditionalAccountsSteps.StepConfirmation:
            const isServiceUser: boolean = yield select(makeSelectAuthoritiesIsServiceUser());
            if (isServiceUser) {
                const commonTranslation: ICommonTranslation = yield select(makeSelectTranslationCommon());
                validateError = commonTranslation.serviceUser.tooltip;
                isStepValid = false;
                break;
            }

            const orderResult: IWizardAdditionalAccountsOrderResult = yield select(
                makeSelectWizardAdditionalAccountsOrderResult(),
            );
            const subscribersWithoutErrors = orderResult.orderSubs.filter(
                (i) => subscribersIds.includes(i.subsId) && ETextBlockStatus.ERROR !== i.status,
            );

            const isReportValid = yield call(wizardReportValidateSaga);
            isStepValid = subscribersIds.length > 0 && subscribersWithoutErrors.length > 0 && isReportValid;
            if (subscribersIds.length < 1) {
                validateError = translation.validateErrors.selectOneSubscribers;
                break;
            }
            if (subscribersWithoutErrors.length < 1) {
                validateError = selectedTypes.isActivatePersonalAccount
                    ? translation.validateErrors.activateAccountsWithErrors
                    : translation.validateErrors.deactivateAccountsWithErrors;
                break;
            }
            validateError = isStepValid ? '' : translation.validateErrors.fillNotify;
            break;
    }
    yield put(
        wizardAdditionalAccountsSetStepValidAction({
            isStepValid,
            validateError,
        }),
    );
}

/*
 * -------------------------------------------
 *              Failure Handler
 * -------------------------------------------
 */

export function* wizardAdditionalAccountsFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        const translationExpensesDetails = yield select(makeSelectTranslationWizardAdditionalAccounts());
        title = translationExpensesDetails.error.codes[errorCode] || translationCommon.error.codes[errorCode];
    }

    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    title,
                    translation: translationCommon.modal,
                }),
        }),
    );
}

/*
 * -------------------------------------------
 *       Wizard Sagas
 * -------------------------------------------
 */

export function* wizardAdditionalAccountsSagas(): SagaIterator {
    yield all([
        // initialization
        takeEvery(WIZARD_START_SUCCESS, wizardAdditionalAccountsStorageSaveSaga),
        takeEvery(WIZARD_META_SUCCESS, wizardAdditionalAccountsMetaSaga),
        // next
        takeEvery(WIZARD_ADDITIONAL_ACCOUNTS_HANDLE_NEXT, wizardAdditionalAccountsHandleNextSaga),
        takeEvery(WIZARD_NEXT_SUCCESS, wizardAdditionalAccountsHandleNextSuccessSaga),
        takeEvery(WIZARD_BACK_SUCCESS, wizardAdditionalAccountsLoadDataFromStorageSaga),
        // validate
        takeEvery(
            [WIZARD_ADDITIONAL_ACCOUNTS_START_VALIDATE, WIZARD_ADDITIONAL_ACCOUNTS_SET_PARAMS],
            wizardAdditionalAccountsValidateSaga,
        ),
        takeEvery(WIZARD_ADDITIONAL_ACCOUNTS_SET_PARAMS, wizardAdditionalAccountsUpdateStorageSaga),
        // failed
        takeEvery(
            [WIZARD_BACK_FAILED, WIZARD_NEXT_FAILED, WIZARD_RESTORE_FAILED, WIZARD_START_FAILED],
            wizardAdditionalAccountsFailedSaga,
        ),
    ]);
}
